import { useMemo, useState } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Shown on step 2 when no rooms are available for the chosen dates.
 * Offers 6 alternative date windows (±3 days) and a "flexible request"
 * dialog that posts to /bookings/flexible-request.
 */
export default function NoAvailabilityBlock({ t, checkIn, checkOut, guests, onSearchAlternative }) {
  const [openRequest, setOpenRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "", flexibility_days: 3 });

  const alternatives = useMemo(() => {
    if (!checkIn || !checkOut) return [];
    const nights = Math.max(1, Math.round((checkOut - checkIn) / 86400000));
    const list = [];
    for (const offset of [-3, -2, -1, 1, 2, 3]) {
      const d1 = new Date(checkIn);
      d1.setDate(d1.getDate() + offset);
      const d2 = new Date(d1);
      d2.setDate(d2.getDate() + nights);
      list.push({ d1, d2, offset });
    }
    return list;
  }, [checkIn, checkOut]);

  const submit = async () => {
    if (!form.name || !form.email) {
      toast.error("Compila nome ed email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings/flexible-request", {
        check_in: format(checkIn, "yyyy-MM-dd"),
        check_out: format(checkOut, "yyyy-MM-dd"),
        guests: parseInt(guests),
        ...form,
      });
      setDone(true);
      toast.success("Richiesta inviata. Ti ricontatteremo entro poche ore.");
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="no-availability-block">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-[#1E3A5F] mb-1">Camere non disponibili per le date selezionate</h3>
          <p className="text-sm text-[#635F59]">
            Non significa che non possiamo accoglierti! Prova date alternative qui sotto, oppure inviaci una richiesta flessibile: il proprietario valuterà personalmente una possibile riorganizzazione delle camere.
          </p>
        </div>
      </div>

      <div>
        <h4 className="font-display text-lg text-[#1E3A5F] mb-3">Date alternative vicine</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {alternatives.map(({ d1, d2, offset }) => (
            <button
              key={offset}
              type="button"
              onClick={() => onSearchAlternative(d1, d2)}
              data-testid={`alt-date-${offset}`}
              className="border border-[#E8E2D9] hover:border-[#AC5D45] bg-white rounded-lg p-3 text-left transition group"
            >
              <div className="text-xs text-[#635F59] mb-1">
                {offset > 0 ? `+${offset} giorni` : `${offset} giorni`}
              </div>
              <div className="text-sm font-semibold text-[#1E3A5F]">
                {format(d1, "dd/MM")} → {format(d2, "dd/MM/yyyy")}
              </div>
              <div className="text-xs text-[#AC5D45] mt-1 group-hover:underline">Verifica disponibilità →</div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#FDFBF7] border border-[#E8E2D9] rounded-xl p-5">
        <h4 className="font-display text-lg text-[#1E3A5F] mb-2">Richiedi comunque per queste date</h4>
        <p className="text-sm text-[#635F59] mb-4">
          Inviaci la tua richiesta: il proprietario può valutare una riorganizzazione delle camere già prenotate per provare ad accoglierti.
        </p>
        <Button
          onClick={() => setOpenRequest(true)}
          data-testid="open-flex-request"
          className="bg-[#1E3A5F] hover:bg-[#152A45] text-white rounded-full"
        >
          Invia richiesta flessibile
        </Button>
      </div>

      <Dialog open={openRequest} onOpenChange={(v) => !v && (done ? (setOpenRequest(false), setDone(false)) : setOpenRequest(false))}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1E3A5F]">
              {done ? "Richiesta inviata" : "Richiesta flessibile"}
            </DialogTitle>
            <DialogDescription>
              {done
                ? "Ti contatteremo via email entro poche ore con una proposta."
                : `Date richieste: ${format(checkIn, "dd/MM/yyyy")} → ${format(checkOut, "dd/MM/yyyy")} · ${guests} ospiti`}
            </DialogDescription>
          </DialogHeader>

          {!done && (
            <div className="space-y-3 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome e cognome *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="flex-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="flex-email" />
                </div>
              </div>
              <div>
                <Label>Telefono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="flex-phone" />
              </div>
              <div>
                <Label>Flessibilità sulle date (± giorni)</Label>
                <Select value={String(form.flexibility_days)} onValueChange={(v) => setForm({ ...form, flexibility_days: parseInt(v) })}>
                  <SelectTrigger className="mt-1.5" data-testid="flex-days"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 5, 7].map((n) => (
                      <SelectItem key={n} value={String(n)}>{n === 0 ? "Solo le date richieste" : `± ${n} giorni`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note per il proprietario</Label>
                <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="mt-1.5" placeholder="Es. abbiamo flessibilità di 1-2 giorni, preferiremmo camera con cucina..." data-testid="flex-note" />
              </div>
            </div>
          )}

          <DialogFooter>
            {done ? (
              <Button onClick={() => { setOpenRequest(false); setDone(false); }} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">Chiudi</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setOpenRequest(false)}>Annulla</Button>
                <Button onClick={submit} disabled={submitting} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="flex-submit">
                  {submitting ? "Invio..." : "Invia richiesta"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
